import { useState, useEffect } from 'react';
import axios from 'axios';
import { CaretDown, Question } from '@phosphor-icons/react';

const API = '/api';

const defaultFAQs = [
  {
    question: "What is the dress code?",
    question_es: "¿Cuál es el código de vestimenta?",
    answer: "The wedding ceremony and reception are Black Tie Optional. This means elegant formal attire - floor-length gowns for women and tuxedos or dark suits for men. Please avoid white or ivory colors.",
    answer_es: "La ceremonia y recepción de la boda son de Etiqueta Opcional. Esto significa atuendo formal elegante - vestidos largos para mujeres y esmoquin o trajes oscuros para hombres. Por favor evite colores blanco o marfil."
  },
  {
    question: "Can I bring a plus one?",
    question_es: "¿Puedo traer un acompañante?",
    answer: "Your invitation will indicate if you have been given a plus one. Please RSVP for all guests in your party.",
    answer_es: "Tu invitación indicará si se te ha dado un acompañante. Por favor confirma asistencia para todos los invitados de tu grupo."
  },
  {
    question: "What airport should I fly into?",
    question_es: "¿A qué aeropuerto debo volar?",
    answer: "Fly into Monseñor Óscar Arnulfo Romero International Airport (SAL) in San Salvador. From there, Lake Coatapeque is approximately 1 hour drive.",
    answer_es: "Vuela al Aeropuerto Internacional Monseñor Óscar Arnulfo Romero (SAL) en San Salvador. Desde allí, el Lago de Coatepeque está aproximadamente a 1 hora en auto."
  }
];

export default function FAQPage() {
  const [faqs, setFaqs] = useState(defaultFAQs);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API}/content/faq`);
        if (response.data && response.data.faqs && response.data.faqs.length > 0) {
          setFaqs(response.data.faqs);
        }
      } catch (error) {
        console.log('Using default FAQs');
      }
    };
    fetchFAQs();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div data-testid="faq-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
            Questions & Answers
          </p>
          <p className="font-body text-xs italic text-stone-400 -mt-3 mb-4">
            Preguntas y Respuestas
          </p>
          <h1 className="page-title">FAQ</h1>
          <p className="font-body text-xs italic text-stone-400">Preguntas Frecuentes</p>
          <p className="page-subtitle mt-4">
            Everything you need to know about our wedding
          </p>
          <p className="font-body text-xs italic text-stone-400">
            Todo lo que necesitas saber sobre nuestra boda
          </p>
        </div>
      </div>

      {/* FAQ List */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-item-${index}`}
                className="bg-white border border-stone-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-6 text-left flex items-center justify-between gap-4 hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <Question size={24} weight="light" className="text-[#B8A88A] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-display text-lg text-stone-900">
                        {faq.question}
                      </h3>
                      {faq.question_es && (
                        <p className="font-body text-sm italic text-stone-400 mt-1">
                          {faq.question_es}
                        </p>
                      )}
                    </div>
                  </div>
                  <CaretDown
                    size={20}
                    weight="bold"
                    className={`text-[#B8A88A] flex-shrink-0 transition-transform duration-200 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {openIndex === index && (
                  <div className="px-6 pb-6 pt-0">
                    <div className="pl-10 border-l-2 border-[#C9B99A] ml-3">
                      <p className="font-body text-stone-600 leading-relaxed">
                        {faq.answer}
                      </p>
                      {faq.answer_es && (
                        <p className="font-body text-sm italic text-stone-400 mt-3 leading-relaxed">
                          {faq.answer_es}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-12 p-8 bg-[#FAF8F5] border border-[#E8E2D9] text-center">
            <Question size={32} weight="light" className="text-[#B8A88A] mx-auto mb-4" />
            <h3 className="font-display text-xl text-stone-900 mb-2">
              Still have questions?
            </h3>
            <p className="font-body text-xs italic text-stone-400 mb-4">
              ¿Aún tienes preguntas?
            </p>
            <p className="font-body text-stone-600 mb-4">
              Feel free to reach out to us directly at:
            </p>
            <a 
              href="mailto:SWRodriguezWedding2026@gmail.com"
              className="font-body text-[#B8A88A] hover:text-[#9A8A6A] transition-colors"
            >
              SWRodriguezWedding2026@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
